import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ApiService from "../../services/api";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import { WhiteLabelButton } from "../../utils/whiteLabelUtils";

type LoginStep = "credentials" | "2fa-setup" | "2fa-verify";
type TwoFAMethod = "email" | "authenticator";

interface TwoFASetupData {
  qr_code?: string;
  secret?: string;
  otp_auth_url?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const {
    contactLink,
    primaryColor,
    isWhiteLabel,
    logoUrl,
    brandName,
    blogs_link,
  } = useWhiteLabel();
  const [step, setStep] = useState<LoginStep>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // 2FA state
  const [twoFAPreferences, setTwoFAPreferences] = useState<TwoFAMethod[]>([]);
  const [selectedTwoFA, setSelectedTwoFA] = useState<TwoFAMethod | "">("");
  const [twoFAMethod, setTwoFAMethod] = useState<TwoFAMethod | "">("");
  const [setupData, setSetupData] = useState<TwoFASetupData>({});

  const handleBackToLogin = () => {
    setStep("credentials");
  };

  const validateEmail = (value: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  };

  const validatePassword = (value: string) => {
    return value.trim().length >= 6;
  };

  const handleInitialLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setEmailError("");
    setPasswordError("");

    let hasError = false;

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      hasError = true;
    }

    if (!validatePassword(password)) {
      setPasswordError("Password must be at least 6 characters");
      hasError = true;
    }

    if (hasError) return;

    setIsLoading(true);

    try {
      const data = await ApiService.login(email, password);

      if (!data.success) {
        throw new Error(data.message || "Login failed");
      }

      if (data.data.require_2fa_setup) {
        // First time login - need to set up 2FA
        const preferences = data.data.two_fa_preferences.map(
          (p: any) => p.value
        );
        setTwoFAPreferences(preferences);
        setStep("2fa-setup");
      } else if (data.data.require_2fa_verification) {
        // 2FA already set up - need to verify
        setTwoFAMethod(data.data.two_fa_method);
        setStep("2fa-verify");

        // If email method, OTP is already sent
        if (data.data.two_fa_method === "email") {
          setError(""); // Clear any previous errors
        }
      } else {
        // Should not happen based on your API, but handle it
        throw new Error("Unexpected response from server");
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid email or password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    if (!selectedTwoFA) {
      setError("Please select a 2FA method");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const data = await ApiService.login(email, password, selectedTwoFA);

      if (!data.success) {
        throw new Error(data.message || "Failed to set up 2FA");
      }

      setTwoFAMethod(data.data.two_fa_method);

      if (data.data.two_fa_method === "authenticator") {
        setSetupData({
          qr_code: data.data.qr_code,
          secret: data.data.secret,
          otp_auth_url: data.data.otp_auth_url,
        });
      }

      setStep("2fa-verify");
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to set up 2FA"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const endpoint =
        twoFAMethod === "email"
          ? "/auth/verify-otp/sign-in/email"
          : "/auth/verify-otp/sign-in/authenticator";

      const data = await ApiService.verifyOtpSignIn(
        endpoint,
        email,
        password,
        otpValue
      );

      if (!data.success) {
        throw new Error(data.message || "Invalid code");
      }

      // Store token and login
      localStorage.setItem("token", data.data.access_token);

      const userObj = await ApiService.getUserDetails();
      await authLogin(userObj?.data); // Update auth context

      if (userObj?.data?.role === "customer") {
        navigate("/patient/dashboard");
      } else if (userObj?.data?.role === "admin") {
        navigate("/admin/dashboard");
      } else if (userObj?.data?.role === "clinic") {
        navigate("/account/dashboard");
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid code. Please try again."
      );
      setOtp(["", "", "", "", "", ""]); // Clear OTP inputs
    } finally {
      setIsLoading(false);
    }
  };

  function HeaderBanner() {
    const [visible, setVisible] = useState(true);

    if (!visible) return null;

    return (
      <div
        className="text-white p-3 sm:p-4 text-center relative"
        style={{ backgroundColor: primaryColor }}
      >
        <button
          onClick={() => setVisible(false)}
          className="absolute right-2 text-lg lg:text-xl sm:right-4 top-3 sm:top-4 text-white hover:text-gray-200 "
        >
          ✕
        </button>
        <p className="text-xs sm:text-sm px-8">
          PGT-P has recently advanced with new clinical applications, improving
          embryo selection and genetic screening outcomes.{" "}
          <a
            href={blogs_link}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Read the full article.
          </a>
        </p>
      </div>
    );
  }

  // Step 1: Email and Password
  if (step === "credentials") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header Banner */}
        {/* <div
          // className="bg-blue-600 text-white p-3 sm:p-4 text-center relative"
          className="text-white p-3 sm:p-4 text-center relative"
          style={{ backgroundColor: primaryColor }}
        >
          <button className="absolute right-2 sm:right-4 top-3 sm:top-4 text-white hover:text-gray-200 text-xl">
            ✕
          </button>
          <p className="text-xs sm:text-sm px-8">
            PGT-P has recently advanced with new clinical applications,
            improving embryo selection and genetic screening outcomes.{" "}
            <a
              href={blogs_link}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Read the full article.
            </a>
          </p>
        </div> */}

        <HeaderBanner />

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-lg w-full space-y-8 bg-white p-3 lg:p-6 sm:p-8 rounded-lg shadow-lg border border-gray-200">
            {/* Logo */}
            <div className="text-center">
              <div className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-lg flex items-center justify-center">
                <img
                  // src="/assets/logo.svg"
                  // alt="Gattaca Genomics Logo"
                  src={logoUrl}
                  alt={`${brandName} Logo`}
                  className="h-100 w-100"
                />
              </div>
              <h2 className="mt-4 sm:mt-6 text-xl sm:text-2xl font-bold text-gray-900">
                Login to your account
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-gray-600">
                Clarity from Conception
              </p>
            </div>

            {/* Form */}
            <form
              // className="mt-6 sm:mt-8 space-y-4 sm:space-y-6"
              className="max-w-md  space-y-6 py-4"
              onSubmit={handleInitialLogin}
            >
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError && validateEmail(e.target.value)) {
                        setEmailError("");
                      }
                    }}
                    className={`appearance-none relative block w-full px-3 py-2.5 sm:py-3 border ${
                      emailError ? "border-red-500" : "border-gray-300"
                    } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm sm:text-base`}
                    placeholder="Enter your email"
                    autoComplete="username"
                  />
                  {emailError && (
                    <p className="text-red-600 text-xs sm:text-sm mt-1">
                      {emailError}
                    </p>
                  )}
                </div>
                <div className="relative w-full">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (passwordError && validatePassword(e.target.value)) {
                        setPasswordError("");
                      }
                    }}
                    className={` block w-full px-3 py-3 border ${
                      passwordError ? "border-red-500" : "border-gray-300"
                    } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 focus:z-10 text-sm sm:text-base pr-10`}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  {/* ICON TOGGLE BUTTON */}
                  <div
                    style={{
                      top: "19px",
                    }}
                    className="absolute right-3 flex items-center cursor-pointer text-gray-300"
                  >
                    {showPassword ? (
                      <EyeOff
                        size={18}
                        onClick={() => setShowPassword(false)}
                      />
                    ) : (
                      <Eye size={18} onClick={() => setShowPassword(true)} />
                    )}
                  </div>
                  {passwordError && (
                    <p className="text-red-600 text-[10px] sm:text-sm absolute lg:top-13  ">
                      {passwordError}
                    </p>
                  )}
                  <div className="flex items-end justify-end">
                    <Link
                      to="/forgot-password"
                      className="font-medium hover:underline text-xs sm:text-sm cursor-pointer"
                      style={{ color: primaryColor }}
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </div>

              {error && (
                <div className="text-red-600 text-xs sm:text-sm text-center">
                  {error}
                </div>
              )}

              <div>
                <WhiteLabelButton
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Signing in..." : "Sign in"}
                </WhiteLabelButton>
              </div>

              <div className="flex items-center justify-between text-xs sm:text-sm">
                <div className="text-center w-full">
                  <span className="text-gray-600">Don't have an account? </span>
                  <Link
                    to="/signup"
                    className="font-medium hover:underline"
                    style={{ color: primaryColor }}
                  >
                    Sign up
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: 2FA Setup (First time)
  if (step === "2fa-setup") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header Banner */}
        <HeaderBanner />

        <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-6 bg-white p-6 sm:p-8 rounded-lg shadow-lg border border-gray-200">
            <div className="text-center">
              <div className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-lg flex items-center justify-center">
                <img
                  // src="/assets/logo.svg"
                  // alt="Gattaca Genomics Logo"
                  src={logoUrl}
                  alt={`${brandName} Logo"`}
                  // className="h-100 w-100"
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/logo.svg";
                  }}
                />
              </div>
              <h2 className="mt-4 text-xl sm:text-2xl font-bold text-gray-900">
                Set up Two-Factor Authentication
              </h2>
              <p className="mt-2 text-xs sm:text-sm text-gray-600">
                Choose your preferred 2FA method for secure login
              </p>
            </div>

            <div className="space-y-4">
              {twoFAPreferences.map((method) => (
                <label
                  key={method}
                  className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedTwoFA === method
                      ? "border-gray-300 bg-gray-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <input
                    type="radio"
                    name="twofa"
                    value={method}
                    checked={selectedTwoFA === method}
                    onChange={(e) =>
                      setSelectedTwoFA(e.target.value as TwoFAMethod)
                    }
                    className="h-4 w-4"
                  />
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">
                      {method === "email" ? "Email" : "Authenticator App"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {method === "email"
                        ? "Receive a code via email"
                        : "Use an app like Google Authenticator"}
                    </p>
                  </div>
                </label>
              ))}
            </div>

            {error && (
              <div className="text-red-600 text-xs sm:text-sm text-center">
                {error}
              </div>
            )}

            <WhiteLabelButton
              onClick={handleSetup2FA}
              disabled={isLoading || !selectedTwoFA}
              className="w-full py-2.5 sm:py-3 px-4 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Setting up..." : "Continue"}
            </WhiteLabelButton>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: 2FA Verification
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header Banner */}
      <HeaderBanner />

      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 bg-white p-3 sm:p-8 rounded-lg shadow-lg border border-gray-200">
          <div className="text-center">
            <div className="mx-auto h-14 w-14 sm:h-16 sm:w-16 rounded-lg flex items-center justify-center">
              <img
                // src="/assets/logo.svg"
                // alt="Gattaca Genomics Logo"
                src={logoUrl}
                alt={`${brandName} Logo"`}
                // className="h-100 w-100"
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.currentTarget.src = "/assets/logo.svg";
                }}
              />
            </div>
            <h2 className="mt-4 text-xl sm:text-2xl font-bold text-gray-900">
              Two-Factor Authentication
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-600">
              {twoFAMethod === "email"
                ? `We've sent a verification code to ${email}`
                : "Enter the code from your authenticator app"}
            </p>
          </div>

          {/* Show QR code for authenticator setup */}
          {setupData.qr_code && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Scan this QR code with your authenticator app
                </p>
                <img
                  src={setupData.qr_code}
                  alt="QR Code"
                  className="mx-auto"
                  width={200}
                  height={200}
                />
                <p className="mt-4 text-xs text-gray-500">
                  Or enter this code manually:
                </p>
                <code className="block mt-2 p-2 bg-gray-100 rounded text-xs break-all">
                  {setupData.secret}
                </code>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter{" "}
                {twoFAMethod === "email" ? "verification" : "authenticator"}{" "}
                code
              </label>
              <div className="flex justify-center space-x-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    style={{
                      width: "100%",
                      maxWidth: "48px",
                    }}
                    className="max-w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300"
                    maxLength={1}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-xs sm:text-sm text-center">
                {error}
              </div>
            )}

            <WhiteLabelButton
              onClick={handleVerifyOtp}
              disabled={isLoading || otp.join("").length !== 6}
              className="w-full text-white py-2.5 sm:py-3 px-4 rounded-md hover:bg-gray-300 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Verifying..." : "Verify"}
            </WhiteLabelButton>

            {/* <p className="text-center text-sm text-gray-600">
              {twoFAMethod === "email"
                ? "Didn't receive the code? "
                : "Can't access your app? "}
              <button
                className="hover:underline font-medium"
                style={{ color: primaryColor }}
              >
                {twoFAMethod === "email" ? "Resend" : "Use backup method"}
              </button>
            </p> */}

            <div className="text-center">
              {/* <p className="mt-4 text-center text-sm text-gray-600"> */}
              {/* Didn't receive the code?{" "} */}
              <button
                type="button"
                onClick={handleBackToLogin}
                className="inline-flex items-center text-xs sm:text-sm font-medium hover:underline"
                style={{ color: primaryColor }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to log in
              </button>
              {/* </p> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
